import React from "react";
import Select from "react-select";
import { FaCheck } from "react-icons/fa";
import { useSelector } from "react-redux";
import { MdClose } from "react-icons/md";

/**
 * Modal dialog for assigning task handlers (Production/Cover)
 *
 * @component
 * @param {Object} props - Component props
 * @param {Function} props.onClose - Function to close the modal
 * @param {Array<Object>} props.users - List of users to select from
 * @param {Object|null} props.selectedUser - Currently selected user (value-label pair)
 * @param {Function} props.setSelectedUser - Setter function for selected user
 * @param {Function} props.confirmAssignment - Callback when assigning a user
 * @param {string} props.activeTab - Indicates the current tab (e.g., "Article", "Cover")
 */
const AssignmentModal = ({
  onClose,
  users,
  selectedUser,
  setSelectedUser,
  confirmAssignment,
  selectedTask,
  activeTab,
}) => {
  const { userData } = useSelector((state) => state.user);

  /**
   * Maps and filters user options for the select dropdown
   * @type {Array<{value: string, label: string, email: string, isCurrentUser: boolean}>}
   */
  const userOptions = users
    .map((user) => {
      const fullName = user.name;
      const isCurrentUser = userData?.id === user.id;

      return {
        value: fullName,
        label: isCurrentUser ? `${fullName} (You)` : fullName,
        email: user.email,
        isCurrentUser,
        id: user.id,
      };
    })
    .sort((a, b) => (b.isCurrentUser ? 1 : 0) - (a.isCurrentUser ? 1 : 0));

  /**
   * Custom option rendering for react-select
   *
   * @param {Object} props
   * @param {Object} props.data - The option data
   * @param {React.Ref} props.innerRef - Ref for DOM
   * @param {Object} props.innerProps - Props for DOM
   * @param {boolean} props.isSelected - Whether this option is selected
   * @returns {JSX.Element}
   */
  const CustomOption = ({ data, innerRef, innerProps, isSelected }) => (
    <div
      ref={innerRef}
      {...innerProps}
      className={`px-3 py-2 flex items-center gap-2 hover:bg-[#E1E6FF] cursor-pointer ${
        isSelected ? "bg-[#E1E6FF]" : ""
      }`}
    >
      {isSelected ? (
        <FaCheck className="text-[#35424D] w-[16px] h-[12px]" />
      ) : (
        <div className="min-w-[16px]" />
      )}
      <div>
        <div className="font-medium text-[#35424D]">{data.label}</div>        
      </div>
    </div>
  );

  let findName = (name, type) => {
    if (name === "Web Handling") {
      if (type === "select") {
        return "Select Web handler";
      } else {
        return "Web handler";
      }
    } else if (name === "TOC Handling") {
      if (type === "select") {
        return "Select TOC handler";
      } else {
        return "TOC handler";
      }
    } else if (name === "Finalizing Handling") {
      if (type === "select") {
        return "Select Finalizing handler";
      } else {
        return "Finalizing handler";
      }
    } else if (name === "Cover Handling") {
      if (type === "select") {
        return "Select Cover handler";
      } else {
        return "Cover handler";
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Modal Header */}
        <div className="flex justify-between items-center border-b px-6 py-4">
          <h2 className="text-lg font-semibold">Assign Handler</h2>
          <button onClick={onClose} aria-label="Close modal">
            <MdClose className="text-[#35424D] w-[16px] h-[16px]" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {activeTab === "Article"
                ? "Production handler"
                : findName(selectedTask.task)}{" "}
              <span className="text-red-500">*</span>
            </label>
            <Select
              options={userOptions}
              value={selectedUser}
              onChange={(option) => setSelectedUser(option)}
              placeholder={
                activeTab === "Article"
                  ? "Select production handler"
                  : findName(selectedTask.task, "select")
              }
              components={{ Option: CustomOption }}
              filterOption={(option, inputValue) => {
                const name = option.label.toLowerCase();
                const email = option.data.email.toLowerCase();
                const input = inputValue.toLowerCase();
                return name.includes(input) || email.includes(input);
              }}
              styles={{
                control: (base, state) => ({
                  ...base,
                  borderColor: state.isFocused ? "#35424D" : "#d1d5db",
                  boxShadow: state.isFocused ? "#35424D" : "none",
                  "&:hover": {
                    borderColor: state.isFocused ? "#35424D" : "#d1d5db",
                  },
                }),
                placeholder: (base) => ({
                  ...base,
                  color: "#6b7280",
                }),
              }}
            />
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200 my-4"></div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3">
            <button
              onClick={confirmAssignment}
              disabled={!selectedUser}
              className={`px-4 py-2 rounded-md text-white transition-colors ${
                !selectedUser
                  ? "bg-blue-300 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              Assign
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignmentModal;
